import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import keyBy from 'lodash/keyBy';
import { selectFeatureFlags } from 'core/userContext';
import { Permission, usePermissionsQuery } from 'api/PermissionsAPI';
import { useWhatsAppMessageChannelFeature } from 'utils/hooks/featureFlags';
import { PermissionsContext } from './PermissionsContext';
import { MergedPermission, PermissionsById } from '../types';
import { createPermissionsMap, messageMediumGroup } from '../constants';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';

export const PermissionsContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [mergedPermissions, setMergedPermissions] = useState<MergedPermission[]>([]);
  const { UseChannelBasedPermissions, EmbeddedMessagingCore } = useSelector(selectFeatureFlags);
  const { isWhatsAppEnabled } = useWhatsAppMessageChannelFeature();
  const isAssetManagerIndexFFEnabled = useFeatureFlag('AssetManagerIndex', false);

  const {
    data: permissionsRaw = {},
    isLoading: isLoadingPermissions,
    refetch: refetchPermissions,
  } = usePermissionsQuery();

  const permissions: Permission[] = permissionsRaw?.permissions || [];

  const permissionsList = UseChannelBasedPermissions
    ? [...permissions, messageMediumGroup]
    : permissions;

  const permissionsMap = createPermissionsMap(
    EmbeddedMessagingCore,
    isWhatsAppEnabled,
    isAssetManagerIndexFFEnabled,
  );

  useEffect(() => {
    if (!isLoadingPermissions) {
      const merged = permissionsList.map((permission) => {
        const currentKey = permissionsMap[permission.id.toString()];
        return {
          ...permission,
          ...currentKey,
          requiredBy: permissions
            .filter((item) => item?.requires?.includes(permission.id)) // get permissions by id
            .map((item) => item.id), // return number[] of permission ids
        };
      });
      setMergedPermissions(merged);
    }
  }, [permissions]);

  const permissionsById = useMemo<PermissionsById>(
    () => (!isLoadingPermissions ? keyBy(mergedPermissions, (permission) => permission.id) : {}),
    [mergedPermissions],
  );

  const getPermissionById = useCallback(
    (id: keyof PermissionsById): MergedPermission => permissionsById[`${id}`],
    [mergedPermissions],
  );

  return (
    <PermissionsContext.Provider
      value={{
        getPermissionById,
        isLoadingPermissions,
        permissions: mergedPermissions,
        permissionsById,
        refetchPermissions,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};
